/**
 * Indirect costs tab — new in v2.
 *
 * Four groups, each with its own subtotal, feeding the P&L separately:
 * Distribution, Advertising & Marketing, Administration, Finance.
 *
 * All values are POSITIVE magnitudes — the P&L applies the sign when it
 * deducts them.
 *
 * 'Other Admin' replaces the template's 'Other Admin 2', which was a balancing
 * plug holding one company's actuals. It is an ordinary input here (spec 7.3).
 */

import { useCallback } from 'react';
import { ComputedDataRow } from '../table/DataRow';
import { V2EditableRow } from './V2EditableRow';
import { GrandTotalRow } from '../table/GrandTotalRow';
import { SectionHeader } from '../table/SectionHeader';
import { SubtotalRow } from '../table/SubtotalRow';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import { V2Sheet } from './V2Table';

export function IndirectCostsView() {
  const model = useFinancialModelV2Store((s) => s.model);
  const updateInput = useFinancialModelV2Store((s) => s.updateInput);

  const handleChange = useCallback(
    (path: string) => (period: string, value: number) => updateInput(path, period, value),
    [updateInput],
  );

  if (!model) return null;
  const { inputs, computed } = model;
  const ic = inputs.indirectCosts;

  const row = (label: string, field: keyof typeof ic) => (
    <V2EditableRow
      label={label}
      data={ic[field] as Record<string, number>}
      onCellChange={handleChange(`indirectCosts.${field}`)}
    />
  );

  return (
    <V2Sheet>
      <SectionHeader label="Distribution" />
      {row('Distribution costs', 'distribution')}

      <SectionHeader label="Advertising & Marketing" />
      {row('Advertising', 'advertising')}
      {row('Marketing', 'marketing')}
      <SubtotalRow
        label="Total Advertising & Marketing"
        data={computed.indirectCosts.totalAdvertisingMarketing}
      />

      <SectionHeader label="Administration" />
      {row('Insurance', 'insurance')}
      {row('Rent', 'rent')}
      {row('Utilities', 'utilities')}
      {row('Salaries', 'salaries')}
      {row('Other Admin', 'otherAdmin')}
      {/* Historical admin includes D&A as filed; forecasts add it from the
          Fixed Assets tab so every column is on the same basis. The P&L then
          takes D&A out of admin, because it is charged below EBITDA. */}
      <ComputedDataRow
        label="Depreciation & Amortisation (forecast, from Fixed Assets)"
        data={computed.indirectCosts.depreciationAmortisation ?? {}}
      />
      <SubtotalRow
        label="Total Administration"
        data={computed.indirectCosts.totalAdministration}
      />

      <SectionHeader label="Finance" />
      {row('Bad Debt', 'badDebt')}
      {row('Bank charges', 'bankCharges')}
      {row('Legal and Prof. Fees', 'legalProfFees')}
      {row('Other Finance costs', 'otherFinanceCosts')}
      <SubtotalRow
        label="Total Finance costs"
        data={computed.indirectCosts.totalFinanceCosts}
      />

      <GrandTotalRow
        label="Total Indirect Costs"
        hint={{ how: 'Total advertising and marketing + total administration + total finance costs (staff costs are added on the P&L).', change: 'The lines of this tab.' }}
        data={computed.indirectCosts.totalIndirectCosts}
      />
    </V2Sheet>
  );
}
